import React, { useEffect, useState } from "react";
import { CreditCard, Check } from "lucide-react";
import api, { arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { CardSkeletonGrid } from "@/components/common/Loading";

function UsageBar({ label, used, limit }) {
  const pct = Math.min(Math.round((used / (limit || 1)) * 100), 100);
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs"><span className="text-slate-500">{label}</span><span className="font-semibold text-slate-700">{used} / {limit}</span></div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function Billing() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/billing").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="space-y-6"><PageHeader title="Billing" /><CardSkeletonGrid /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title="Billing & Subscription" subtitle="Your current plan, usage and invoices" icon={CreditCard} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="card-surface p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-overline">Current Plan</p>
              <p className="mt-1 font-heading text-2xl font-bold text-slate-900">{data.plan}</p>
              <p className="text-sm text-slate-500">₹{(data.monthlyPrice || 0).toLocaleString()}/month</p>
            </div>
            <div className="flex flex-col items-end gap-2"><StatusBadge status={data.status} /><StatusBadge status={data.paymentStatus} /></div>
          </div>
          <p className="mt-3 text-sm text-slate-500">Renews / expires on {data.subscriptionEnd ? new Date(data.subscriptionEnd).toLocaleDateString() : "—"}</p>
          <div className="mt-5 space-y-3">
            <UsageBar label="Posts" used={data.usage?.posts || 0} limit={data.limits?.posts} />
            <UsageBar label="Review Requests" used={data.usage?.reviewRequests || 0} limit={data.limits?.reviewRequests} />
            <UsageBar label="AI Replies" used={data.usage?.aiReplies || 0} limit={data.limits?.aiReplies} />
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="font-heading text-base font-semibold text-slate-800">Invoices</h3>
          <div className="mt-3 space-y-2">
            {arr(data.invoices).length === 0 && <p className="text-sm text-slate-400">No invoices yet</p>}
            {arr(data.invoices).map((h, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{new Date(h.date).toLocaleDateString()}</span>
                <span className="font-semibold">₹{h.amount}</span>
                <StatusBadge status={h.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-heading text-lg font-semibold text-slate-800">Available Plans</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {arr(data.plans).map((p) => (
            <div key={p._id} className={`card-surface flex flex-col p-6 ${p.name === data.plan ? "ring-2 ring-brand-500" : ""}`}>
              <p className="text-overline">{p.name}</p>
              <p className="mt-2 font-heading text-2xl font-bold text-slate-900">₹{p.price.toLocaleString()}<span className="text-sm font-normal text-slate-400">/mo</span></p>
              <ul className="mt-4 flex-1 space-y-2">{arr(p.features).map((f, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-600"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" /> {f}</li>)}</ul>
              {p.name === data.plan && <span className="mt-4 rounded-xl bg-brand-50 py-2 text-center text-sm font-semibold text-brand-700">Current Plan</span>}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">To upgrade or change your plan, please contact AWebBee support.</p>
      </div>
    </div>
  );
}
